import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { ProjectArchitectureFlow } from '../diagrams/ProductArchitectureFlow';

const PROJECTS_PARENT_URL = `${process.env.PUBLIC_URL || ''}/projects/index.json`;

function SectionBody({ sec }) {
  return (
    <>
      {sec.explanation && (
        <p className="font-body text-text-body leading-relaxed">{sec.explanation}</p>
      )}
      {Array.isArray(sec.bullets) && sec.bullets.length > 0 && (
        <ul className="mt-3 space-y-2">
          {sec.bullets.map((b, j) => (
            <li key={j} className="flex items-start gap-2.5 font-body text-text-body text-sm leading-relaxed">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-cyan" />
              {b}
            </li>
          ))}
        </ul>
      )}
      {Array.isArray(sec.tags) && sec.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {sec.tags.map((tag, j) => (
            <span
              key={j}
              className="font-mono text-[0.65rem] uppercase tracking-wider px-2.5 py-1 rounded-full"
              style={{ background: 'var(--jb-cyan-dim)', color: 'var(--jb-cyan)' }}
            >
              {tag}
            </span>
          ))}
        </div>
      )}
      {sec.highlight && (
        <div
          className="mt-4 p-4 rounded-xl border"
          style={{ background: 'rgba(0,229,204,0.04)', borderColor: 'rgba(0,229,204,0.2)' }}
        >
          {sec.highlight.label && (
            <p className="font-mono text-[0.65rem] uppercase tracking-widest text-cyan mb-1">{sec.highlight.label}</p>
          )}
          <p className="font-body text-text-body text-sm leading-relaxed">{sec.highlight.text}</p>
        </div>
      )}
      {sec.imageUrl && (
        <figure className="mt-4 rounded-xl overflow-hidden border bg-forest-card p-4" style={{ borderColor: 'var(--jb-border)' }}>
          <img src={sec.imageUrl} alt="" className="w-full h-auto" />
        </figure>
      )}
      {sec.drawioUrl && (
        <div className="mt-4 rounded-xl overflow-hidden border bg-forest-card p-4" style={{ borderColor: 'var(--jb-border)' }}>
          <iframe title="Diagram" src={sec.drawioUrl} className="w-full border-0 min-h-[400px]" style={{ height: '500px' }} />
        </div>
      )}
    </>
  );
}

/**
 * Project detail (#project/slug). Fetches parent projects/index.json for contentPath, then child JSON.
 */
export default function ProjectDetail({ slug, onBack }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(!!slug);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(PROJECTS_PARENT_URL)
      .then((res) => { if (!res.ok) throw new Error('Parent not found'); return res.json(); })
      .then((parent) => {
        const item = (parent.items || []).find((i) => i.slug === slug);
        const contentPath = item?.contentPath;
        if (!contentPath || cancelled) { if (!contentPath) setError('Not found'); setLoading(false); return; }
        const url = contentPath.startsWith('http') ? contentPath : `${process.env.PUBLIC_URL || ''}${contentPath}`;
        return fetch(url).then((r) => { if (!r.ok) throw new Error('Not found'); return r.json(); });
      })
      .then((data) => { if (data && !cancelled) setDetail(data); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-forest-bg pt-24 pb-12 flex items-center justify-center">
        <p className="font-body text-text-sub">Loading…</p>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="min-h-screen bg-forest-bg pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-4">
          <button type="button" onClick={onBack} className="inline-flex items-center gap-2 font-body font-medium text-text-sub hover:text-cyan mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <p className="font-body text-text-sub">Project not found.</p>
        </div>
      </div>
    );
  }

  const title = detail.title ?? '';
  const introduction = detail.introduction ?? '';
  const sections = detail.sections ?? [];

  return (
    <div className="min-h-screen bg-forest-bg relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 35% at 50% 0%, rgba(34,197,94,.04) 0%, transparent 70%)' }}
        aria-hidden="true"
      />
      <article className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-16">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 font-body font-medium text-text-sub hover:text-cyan mb-8 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Work
        </button>

        <header className="mb-10">
          <span
            className="inline-block font-mono text-[0.65rem] uppercase tracking-wider px-2.5 py-1 rounded-full mb-4"
            style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E' }}
          >
            {detail.eyebrow || 'Client delivery'}
          </span>
          {detail.client && (
            <p className="font-body text-sm text-text-muted mb-2">{detail.client}</p>
          )}
          <h1 className="font-display font-black text-3xl sm:text-4xl text-text-hero leading-tight mb-3">{title}</h1>
          {introduction && (
            <p className="font-body text-lg text-text-sub leading-relaxed">{introduction}</p>
          )}
        </header>

        {slug && (
          <div className="mb-12">
            <ProjectArchitectureFlow slug={slug} />
          </div>
        )}

        <div className="space-y-10">
          {sections.map((sec, i) => (
            <section key={i} className="border-t pt-8" style={{ borderColor: 'rgba(0,229,204,0.1)' }}>
              <h2 className="font-display font-bold text-lg text-cyan mb-1">{sec.heading}</h2>
              {sec.subHeading && (
                <h3 className="font-body font-semibold text-text-body text-sm mb-2 uppercase tracking-wider">{sec.subHeading}</h3>
              )}
              <SectionBody sec={sec} />
            </section>
          ))}
        </div>
      </article>
    </div>
  );
}
